export const HALL_WIDTH_M = 60;
export const HALL_HEIGHT_M = 32;

export const SHOPFLOOR_WIDGET_DND = 'application/x-shopfloor-widget';

export const SHOPFLOOR_WIDGET_TYPES = ['stacklight', 'temperature', 'sml_meter', 'switch'] as const;
export type ShopfloorWidgetType = (typeof SHOPFLOOR_WIDGET_TYPES)[number];

export interface PlacedSignal {
  id: string;
  type: ShopfloorWidgetType;
  catalogId?: string;
  /** Server signals file name (stem). Required for stacklight. */
  signalName?: string;
  /** Stacklight instance id on `hostname` (same binding as a dashboard widget). */
  stacklight?: string;
  hostname?: string;
  topic?: string;
  topicRh?: string;
  topicPower?: string;
  topicReverse?: string;
  label: string;
  /** switch widget: toggle or bulb. */
  icon?: 'switch' | 'bulb' | string;
  /** Relay channel command target. A bulb leaves these unset for commands. */
  of?: string;
  key?: string;
  x: number;
  y: number;
}

export function isShopfloorWidgetType(value: string | undefined): value is ShopfloorWidgetType {
  return value === 'stacklight'
    || value === 'temperature'
    || value === 'sml_meter'
    || value === 'switch';
}

export function isShopfloorCatalogItem(item: {
  type?: string;
  topic?: string;
  signalName?: string;
}): boolean {
  if (item.type === 'stacklight') {
    return Boolean(String(item.signalName || '').trim());
  }
  if (item.type === 'temperature' || item.type === 'sml_meter' || item.type === 'switch') {
    return Boolean(String(item.topic || '').trim());
  }
  return false;
}

export function normalizePlacedSignal(raw: Partial<PlacedSignal>): PlacedSignal | null {
  const signalName = String(raw.signalName || '').trim();
  const topic = String(raw.topic || '').trim();
  const type = isShopfloorWidgetType(raw.type)
    ? raw.type
    : (signalName ? 'stacklight' : 'temperature');
  if (type === 'stacklight' && !signalName) {
    return null;
  }
  if ((type === 'temperature' || type === 'sml_meter' || type === 'switch') && !topic) {
    return null;
  }
  return {
    id: String(raw.id || ''),
    type,
    catalogId: raw.catalogId != null && String(raw.catalogId).trim()
      ? String(raw.catalogId)
      : undefined,
    signalName: signalName || undefined,
    stacklight: raw.stacklight != null && String(raw.stacklight).trim()
      ? String(raw.stacklight).trim()
      : undefined,
    hostname: raw.hostname != null && String(raw.hostname).trim()
      ? String(raw.hostname).trim()
      : undefined,
    topic: topic || undefined,
    topicRh: raw.topicRh != null && String(raw.topicRh).trim() ? String(raw.topicRh) : undefined,
    topicPower: raw.topicPower != null && String(raw.topicPower).trim()
      ? String(raw.topicPower)
      : undefined,
    topicReverse: raw.topicReverse != null && String(raw.topicReverse).trim()
      ? String(raw.topicReverse)
      : undefined,
    icon: raw.icon === 'bulb' || raw.icon === 'switch' ? raw.icon : undefined,
    of: raw.of != null && String(raw.of).trim() ? String(raw.of).trim() : undefined,
    key: raw.key != null && String(raw.key).trim() ? String(raw.key).trim() : undefined,
    label: String(raw.label || signalName || topic),
    x: Number(raw.x) || 0,
    y: Number(raw.y) || 0,
  };
}
